import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, CheckCircle, FileSearch, AlertTriangle } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const PAYMENT_POLL_MAX = 20;
const PAYMENT_POLL_INTERVAL = 2500;
const ANALYSIS_POLL_MAX = 40;
const ANALYSIS_POLL_INTERVAL = 2500;

const PaymentSuccess = () => {
  const { user, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [phase, setPhase] = useState('verifying');
  const [contractId, setContractId] = useState(null);
  const [statusMsg, setStatusMsg] = useState('');
  const pollingRef = useRef(false);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    if (!sessionId) { navigate('/dashboard'); return; }
    if (pollingRef.current) return;
    pollingRef.current = true;
    console.log('[PaymentSuccess] Starting payment polling for session:', sessionId);
    setStatusMsg('Conectando con Stripe...');
    pollPaymentStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, authLoading, user]);

  // ── FASE 1: Polling de pago ──
  const pollPaymentStatus = async (attempts = 0) => {
    console.log(`[PaymentSuccess] Waiting for payment... (attempt ${attempts + 1}/${PAYMENT_POLL_MAX})`);
    if (attempts >= PAYMENT_POLL_MAX) {
      console.error('[PaymentSuccess] Payment verification timed out');
      setPhase('error');
      setStatusMsg('Tiempo de espera agotado verificando el pago.');
      return;
    }
    try {
      const res = await api.getPaymentStatus(sessionId);
      console.log('[PaymentSuccess] Payment status response:', JSON.stringify(res));

      const isPaid = res.payment_status === 'completed' || res.status === 'paid';
      const isExpired = res.status === 'expired';

      if (isPaid) {
        console.log('[PaymentSuccess] Payment confirmed!');
        const cid = res.contract_id;
        const isComparison = (res.package_id || '').startsWith('comparison_');

        // Brief "paid" phase for visual feedback
        setPhase('paid');
        setStatusMsg('Pago confirmado. Preparando siguiente paso...');

        if (isComparison && res.contract_ids && res.contract_ids.length > 0) {
          console.log('[PaymentSuccess] Comparison flow — redirecting to compare page');
          toast.success('Pago confirmado. Redirigiendo a comparacion...');
          const ids = res.contract_ids.join(',');
          setTimeout(() => navigate(`/compare?contract_ids=${ids}&auto=true`), 1500);
        } else if (cid) {
          setContractId(cid);
          console.log(`[PaymentSuccess] Contract analysis flow — contract_id: ${cid}`);
          toast.success('Pago confirmado. Iniciando analisis de tu contrato...');
          // Short pause so the user sees "Pago Confirmado" before moving to analysis phase
          setTimeout(() => {
            setPhase('analyzing');
            setStatusMsg('Esperando que el analisis inicie...');
            pollContractStatus(cid);
          }, 1200);
        } else {
          console.warn('[PaymentSuccess] Payment confirmed but no contract_id in response');
          toast.success('Pago confirmado.');
          setPhase('done');
          setStatusMsg('');
        }
      } else if (isExpired) {
        console.error('[PaymentSuccess] Payment session expired');
        setPhase('error');
        setStatusMsg('La sesion de pago ha expirado.');
      } else {
        setStatusMsg(`Verificando pago... (intento ${attempts + 1})`);
        setTimeout(() => pollPaymentStatus(attempts + 1), PAYMENT_POLL_INTERVAL);
      }
    } catch (err) {
      console.error('[PaymentSuccess] Error polling payment:', err?.message || err);
      setTimeout(() => pollPaymentStatus(attempts + 1), PAYMENT_POLL_INTERVAL + 500);
    }
  };

  // ── FASE 2: Polling del contrato hasta ANALYZED ──
  const pollContractStatus = async (cid, attempts = 0) => {
    console.log(`[PaymentSuccess] Waiting for analysis... (attempt ${attempts + 1}/${ANALYSIS_POLL_MAX}) contract_id: ${cid}`);
    if (attempts >= ANALYSIS_POLL_MAX) {
      console.warn('[PaymentSuccess] Analysis polling timed out — redirecting to dashboard');
      toast.info('El analisis esta tardando mas de lo esperado. Revisa tu dashboard.');
      navigate('/dashboard');
      return;
    }
    try {
      const contracts = await api.getContracts();
      const contract = contracts.find(c => c.id === cid);
      const currentStatus = contract?.status || 'not_found';
      console.log(`[PaymentSuccess] Contract ${cid} current status: ${currentStatus}`);

      if (contract && contract.status === 'analyzed') {
        console.log('[PaymentSuccess] Analysis completed! Redirecting to analysis view.');
        toast.success('Analisis completado!');
        setPhase('done');
        setStatusMsg('Redirigiendo al analisis...');
        setTimeout(() => navigate(`/analysis/${cid}`), 800);
        return;
      }
      if (contract && contract.status === 'failed') {
        console.error('[PaymentSuccess] Analysis failed for contract:', cid);
        toast.error('Error en el analisis. Revisa tu dashboard.');
        navigate('/dashboard');
        return;
      }
      // Still uploaded or processing — keep polling
      if (contract && contract.status === 'processing') {
        setStatusMsg('IA analizando tu contrato...');
      } else {
        setStatusMsg('Esperando inicio del analisis...');
      }
      setTimeout(() => pollContractStatus(cid, attempts + 1), ANALYSIS_POLL_INTERVAL);
    } catch (err) {
      console.error('[PaymentSuccess] Error polling contract status:', err?.message || err);
      setTimeout(() => pollContractStatus(cid, attempts + 1), ANALYSIS_POLL_INTERVAL + 500);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F5F0' }} data-testid="payment-success-loading">
        <Loader2 className="w-12 h-12 animate-spin" style={{ color: '#1A4D2E' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F5F0' }} data-testid="payment-success-page">
      <div className="w-full max-w-md px-4">
        <div className="bg-white border border-border shadow-sm rounded-sm p-8 text-center">

          {/* ── Verifying Payment ── */}
          {phase === 'verifying' && (
            <div data-testid="phase-verifying">
              <Loader2 className="w-16 h-16 mx-auto mb-4 animate-spin" style={{ color: '#1A4D2E' }} />
              <h2 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
                Verificando Pago
              </h2>
              <p className="text-base" style={{ color: '#64748B' }}>{statusMsg || 'Conectando con Stripe...'}</p>
            </div>
          )}

          {/* ── Payment Confirmed (brief) ── */}
          {phase === 'paid' && (
            <div data-testid="phase-paid">
              <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#1A4D2E' }} />
              <h2 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#1A4D2E' }}>
                Pago Confirmado
              </h2>
              <p className="text-base" style={{ color: '#64748B' }}>{statusMsg || 'Preparando analisis...'}</p>
            </div>
          )}

          {/* ── Analyzing Contract ── */}
          {phase === 'analyzing' && (
            <div data-testid="phase-analyzing">
              <FileSearch className="w-16 h-16 mx-auto mb-4 animate-pulse" style={{ color: '#1A4D2E' }} />
              <h2 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#1A4D2E' }}>
                Analizando Contrato
              </h2>
              <p className="text-base mb-2" style={{ color: '#64748B' }}>
                Nuestro sistema de IA esta revisando tu contrato...
              </p>
              <div className="flex items-center justify-center gap-2 mt-4">
                <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#1A4D2E' }} />
                <span className="text-sm" style={{ color: '#64748B' }}>{statusMsg || 'Esto puede tomar 15-30 segundos'}</span>
              </div>
              {contractId && (
                <p className="text-xs mt-3" style={{ color: '#94A3B8' }} data-testid="contract-id-display">
                  ID: {contractId}
                </p>
              )}
            </div>
          )}

          {/* ── Done ── */}
          {phase === 'done' && (
            <div data-testid="phase-done">
              <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#1A4D2E' }} />
              <h2 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#1A4D2E' }}>
                Proceso Exitoso
              </h2>
              <p className="text-base mb-6" style={{ color: '#64748B' }}>
                {statusMsg || 'Tu contrato ha sido procesado correctamente.'}
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full py-3 rounded font-semibold text-white"
                style={{ backgroundColor: '#1A4D2E' }}
                data-testid="go-to-dashboard-btn"
              >
                Ir al Dashboard
              </button>
            </div>
          )}

          {/* ── Error ── */}
          {phase === 'error' && (
            <div data-testid="phase-error">
              <AlertTriangle className="w-16 h-16 mx-auto mb-4" style={{ color: '#D97706' }} />
              <h2 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
                Verificacion Pendiente
              </h2>
              <p className="text-base mb-6" style={{ color: '#64748B' }}>
                {statusMsg || 'No pudimos verificar tu pago. Revisa tu historial.'}
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full py-3 rounded font-semibold text-white"
                style={{ backgroundColor: '#1A4D2E' }}
                data-testid="go-to-dashboard-error-btn"
              >
                Ir al Dashboard
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
